import { combineReducers } from 'redux'

import { IForm } from '@features/forms'

import initialState from './initial_state'
import {
  INIT_FORM,
  SET_FORM_ERRORS,
  SET_FIELD_ERRORS,
  FOCUS_FIELD,
  TOUCH_FIELD,
  UNTOUCH_FIELD,
  TOUCH_ALL_FIELDS,
  UPDATE_FIELD,

  // Stepped Form
  INIT_STEPPED_FORM,
  START_UPDATING,
  STOP_UPDATING,
  GOTO_PREV_STEP,
  GOTO_NEXT_STEP
} from './constants'

interface IInitFormPayload {
  /** ID to reference the form with */
  id: string
  initialState: IForm
}

export default combineReducers({
  byId: (state = initialState.byId, action: any) => {
    let handlers: any = {
      [INIT_FORM]: (state: any, { id, initialState }: IInitFormPayload) => {
        return {
          ...state,
          [id]: {
            id: id,
            errors: null,
            fields: initialState
          }
        }
      },

      [SET_FORM_ERRORS]: (state: any, payload: any) => {
        let { id } = payload
        let errors = payload.errors || {}
        let form: IForm = state[id]

        if (Object.values(errors).length < 1) {
          errors = null
        }

        return {
          ...state,
          [id]: {
            ...form,
            errors: errors
          }
        }
      },

      [SET_FIELD_ERRORS]: (state: any, payload: any) => {
        let { id } = payload
        let errors = payload.errors || {}
        let form: IForm = state[id]
        let newFields = Object.values(form.fields).reduce((acc, field) => {
          return {
            ...acc,
            [field.name]: {
              ...field,
              error: errors[field.name] || null
            }
          }
        }, {})

        return {
          ...state,
          [id]: {
            ...form,
            fields: newFields
          }
        }
      },

      [FOCUS_FIELD]: (state: any, { id, name }: any) => {
        const form = state[id]

        return {
          ...state,
          [id]: {
            ...form,
            fields: {
              ...form.fields,
              [name]: {
                ...form.fields[name],
                focussed: true
              }
            }
          }
        }
      },

      [TOUCH_FIELD]: (state: any, { id, name }: any) => {
        const form = state[id]

        return {
          ...state,
          [id]: {
            ...form,
            fields: {
              ...form.fields,
              [name]: {
                ...form.fields[name],
                focussed: false,
                touched: true
              }
            }
          }
        }
      },

      [UNTOUCH_FIELD]: (state: any, { id, name }: any) => {
        const form = state[id]

        return {
          ...state,
          [id]: {
            ...form,
            fields: {
              ...form.fields,
              [name]: {
                ...form.fields[name],
                focussed: false,
                touched: false
              }
            }
          }
        }
      },

      [TOUCH_ALL_FIELDS]: (state: any, { id, fields }: { id: string; fields?: string[] }) => {
        const form = state[id]
        const formFields = form.fields
        const updatedFields: any = {}
        for (const name in formFields) {
          if (Object.prototype.hasOwnProperty.call(formFields, name)) {
            // Only touch fields if applicable
            if (fields && fields.indexOf(name) < 0) {
              updatedFields[name] = formFields[name]
              continue
            }
            updatedFields[name] = {
              ...formFields[name],
              touched: true
            }
          }
        }

        return {
          ...state,
          [id]: {
            ...form,
            fields: updatedFields
          }
        }
      },

      [UPDATE_FIELD]: (state: any, { id, name, value }: any) => {
        const form = state[id]

        return {
          ...state,
          [id]: {
            ...form,
            fields: {
              ...form.fields,
              [name]: {
                ...form.fields[name],
                value
              }
            }
          }
        }
      },

      /*
        Stepped Form
      */

      [INIT_STEPPED_FORM]: (state: any, { id, initialState }: IInitFormPayload) => {
        return {
          ...state,
          [id]: {
            id: id,
            errors: null,
            fields: initialState,
            activeStepIndex: 0,
            isUpdating: false
          }
        }
      },

      [START_UPDATING]: (state: any, { id }: any) => {
        return {
          ...state,
          [id]: {
            ...state[id],
            isUpdating: true
          }
        }
      },
      [STOP_UPDATING]: (state: any, { id }: any) => {
        return {
          ...state,
          [id]: {
            ...state[id],
            isUpdating: false
          }
        }
      },
      [GOTO_PREV_STEP]: (state: any, { id }: any) => {
        return {
          ...state,
          [id]: {
            ...state[id],
            activeStepIndex: state[id].activeStepIndex - 1
          }
        }
      },
      [GOTO_NEXT_STEP]: (state: any, { id }: any) => {
        return {
          ...state,
          [id]: {
            ...state[id],
            activeStepIndex: state[id].activeStepIndex + 1
          }
        }
      }
    }

    let handler = handlers[action.type]

    if (typeof handler === 'function') {
      return handler(state, action.payload)
    }

    return state
  }
})
